'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { DOCUMENT_HTML_LANG, type LangCode } from '@/lib/i18n/lang';
import sr from './translations/sr';
import srCyrl from './translations/srCyrl';
import en from './translations/en';
import ru from './translations/ru';
import tr from './translations/tr';
import de from './translations/de';

export type { LangCode } from '@/lib/i18n/lang';

export interface Language {
  code: LangCode;
  label: string;
  flag: string;
  /** Kratka oznaka u prekidaču jezika (izbegava „SR-CYRL“ u UI). */
  navAbbrev?: string;
}

export const LANGUAGES: Language[] = [
  { code: 'sr', label: 'Srpski (latinica)', flag: '🇷🇸' },
  { code: 'sr-cyrl', label: 'Српски (ћирилица)', flag: '🇷🇸', navAbbrev: 'Ћир.' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

const translations: Record<LangCode, Record<string, string>> = {
  sr,
  'sr-cyrl': srCyrl,
  en,
  ru,
  tr,
  de,
};

interface LanguageContextType {
  lang: LangCode;
  setLang: (code: LangCode) => void;
  t: (key: string) => string;
  currentLanguage: Language;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'sr',
  setLang: () => {},
  t: (key) => key,
  currentLanguage: LANGUAGES[0],
});

export const useLanguage = () => useContext(LanguageContext);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<LangCode>('sr');

  const setLang = useCallback((code: LangCode) => {
    setLangState(code);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = DOCUMENT_HTML_LANG[code] ?? code;
    }
  }, []);

  const t = useCallback(
    (key: string): string => {
      return (
        translations[lang]?.[key] ??
        (lang === 'sr-cyrl' ? translations['sr']?.[key] : undefined) ??
        translations['en']?.[key] ??
        key
      );
    },
    [lang]
  );

  const currentLanguage = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, currentLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
